/**
 * WorkflowActionButton
 * Renders a single action button from a WorkflowAction, resolving it via the registry.
 * Disabled when allPreconditionsSatisfied is false (BR-JO-08).
 */

import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { resolveAction } from '../../lib/workflow-action-registry';
import { useWorkflowPanel } from './useWorkflowPanel';
import type { WorkflowAction } from '../../types/workflow';

interface WorkflowActionButtonProps {
  action: WorkflowAction;
  disabled?: boolean;
  testId?: string;
  size?: 'sm' | 'default';
  /** Suppress the min-h-[44px] touch-target lift for dense desktop contexts (e.g. the rail). */
  compact?: boolean;
}

export function WorkflowActionButton({
  action,
  disabled = false,
  testId = 'workflow-outcome-action-btn',
  size = 'sm',
  compact = false,
}: WorkflowActionButtonProps) {
  const navigate = useNavigate();
  const { openModal, closePanel } = useWorkflowPanel();
  const descriptor = resolveAction(action);

  const handleClick = () => {
    if (disabled) return;
    const { effect } = descriptor;
    if (effect.kind === 'navigate') {
      // Collapse the rail / close the mobile sheet BEFORE navigating, so the user
      // actually lands on the destination instead of staring at the still-open panel.
      closePanel();
      navigate(effect.url);
    } else {
      openModal(effect.modalKey, effect.params);
    }
  };

  return (
    <Button
      variant="default"
      size={size}
      disabled={disabled}
      onClick={handleClick}
      // size="sm" is h-8 (32px). min-h-[44px] lifts the touch target for mobile; compact mode
      // skips this for dense desktop contexts where a 32px button is appropriate.
      className={compact ? '' : 'min-h-[44px]'}
      data-testid={testId}
      aria-label={descriptor.label}
      aria-disabled={disabled}
    >
      {descriptor.label}
    </Button>
  );
}
